import React from "react";
import { Grid, Typography, Theme } from "@material-ui/core";
import { FormattedDate, useIntl } from "react-intl";
import { makeStyles } from "@material-ui/styles";
import { FormattedMessage } from "react-intl";
import { useCommonWidths } from "../../../hooks";
import {
	InvoicesResource,
	PaymentNoticesResource,
	BackofficeApplicationResource,
} from "../../../resources";
import { MyPagePaper, MyPagePanel } from "../../common";
import { InvoiceStatusEnum } from "../../../utils";
import { InvoiceTable } from "../../common/InvoiceTable";

const useStyles = makeStyles((theme: Theme) => ({
	panelWrapper: {
		margin: "0 auto",
		maxWidth: 1500,
	},
	paperWrapperMarginTop: {
		marginTop: theme.spacing(2),
	},
	headings: {
		fontWeight: "bold",
	},
	label: {
		fontWeight: "bold",
		color: "grey",
	},
	value: {
		fontWeight: "bold",
	},
}));

interface InvoiceTabPageProps {
	backofficeApplication: BackofficeApplicationResource;
	invoices: InvoicesResource[];
	paymentNotices: PaymentNoticesResource[];
}

export const InvoicesTabPage = (props: InvoiceTabPageProps) => {
	const classes = useStyles();

	const { backofficeApplication, invoices, paymentNotices } = props;

	const collectedInvoicesAndNotices: any[] = [];
	collectedInvoicesAndNotices.push(...invoices);
	collectedInvoicesAndNotices.push(...paymentNotices);
	collectedInvoicesAndNotices
		.sort((a, b) => {
			//Sorter med eldste først:
			return (
				new Date(a.dueDate || a.dateSent).getTime() -
				new Date(b.dueDate || b.dateSent).getTime()
			);
		})
		.reverse();

	const isMobile = useCommonWidths().isSmDown;

	const compareByDateReturnMostRecent = (
		i1: InvoicesResource,
		i2: InvoicesResource
	) => {
		const dueDate1 = new Date(i1.dueDate || "01/01/2000").getTime();
		const dueDate2 = new Date(i2.dueDate || "01/01/2000").getTime();
		if (dueDate1 < dueDate2) {
			return 1;
		}
		if (dueDate1 > dueDate2) {
			return -1;
		}
		return 0;
	};

	const newestInvoice = invoices.sort(compareByDateReturnMostRecent)[0];

	const intl = useIntl();
	let sumToPay = 0;
	invoices
		.filter(
			(i) =>
				i.backofficeApplicationId === backofficeApplication.id &&
				[InvoiceStatusEnum.ToPay, InvoiceStatusEnum.PartiallyPaid].includes(
					i.status
				)
		)
		.forEach((i) => {
			sumToPay += i.remainingBalance;
		});

	return (
		<MyPagePanel>
			<Grid item container direction="column" spacing={isMobile ? 4 : 6}>
				{newestInvoice && (
					<Grid item xs={12}>
						<Grid
							container
							direction="row"
							justify="space-between"
							alignItems="baseline"
						>
							<Typography
								variant={isMobile ? "h6" : "h4"}
								className={classes.headings}
							>
								{backofficeApplication.fulfilled ? (
									<FormattedMessage
										id="minside.backofficeApplications.invoicedata.last_paid_invoice"
										defaultMessage="Siste betalte faktura"
										description="Last paid invoice label"
									/>
								) : (
									<FormattedMessage
										id="minside.backofficeApplications.invoicedata.remaining"
										defaultMessage="Å betale"
										description="Remaining amount to pay label"
									/>
								)}
							</Typography>
						</Grid>
						<MyPagePaper className={classes.paperWrapperMarginTop}>
							<Grid container direction="row" justify="space-between">
								<Grid item>
									<Typography variant="caption">
										<FormattedMessage
											id="minside.backofficeApplications.invoicedata.sum_header"
											defaultMessage="Utfakturerte, ikke betalte poster:"
											description="Due date label"
										/>
									</Typography>
									<Typography
										variant={isMobile ? "body1" : "h5"}
										className={classes.value}
									>
										<FormattedMessage
											id="minside.backofficeApplications.invoicedata.sumToPay_nextInvoice"
											defaultMessage="{sumToPay}"
											values={{
												sumToPay: Intl.NumberFormat(intl.locale, {
													style: "currency",
													currency: "NOK",
													maximumFractionDigits: 0,
													minimumFractionDigits: 0,
												}).format(sumToPay),
											}}
										/>
									</Typography>
								</Grid>
								<Grid item>
									<LastInvoicesToPayInformation
										label="Kontonr."
										value="2220 19 95578"
									/>
								</Grid>
								<Grid item>
									<LastInvoicesToPayInformation
										label="Kidnr."
										value={backofficeApplication.kidNrFromLastInvoice || " - "}
									/>
								</Grid>

								{/* <Grid item style={{ alignSelf: "center" }}>
                            {// TODO: Implement screen for transactiondetails}
                            <MyPageButton nextIcon floatRight>Se faktura</MyPageButton>
                        </Grid> */}
							</Grid>
						</MyPagePaper>
					</Grid>
				)}

				<Grid item xs={12}>
					<Typography
						variant={isMobile ? "h6" : "h4"}
						className={classes.headings}
						gutterBottom
					>
						<FormattedMessage
							id="minside.loans.tables.previous_invoices_and_notices.header"
							defaultMessage="Tidligere fakturaer"
							description="Previous Invoices &amp; PaymentNotices header"
						/>
					</Typography>
					<Typography
						style={{ fontWeight: 600 }}
						gutterBottom
						variant={isMobile ? "body1" : "h6"}
					>
						<FormattedMessage
							id="minside.loans.tables.previous_invoices_and_notices.body"
							defaultMessage="Vi gjør oppmerksom på at vi dekker opp den eldste ubetalte fakturaen først"
							description="Previous Invoices &amp; PaymentNotices body"
						/>
					</Typography>
					<MyPagePaper className={classes.paperWrapperMarginTop}>
						<InvoiceTable
							backofficeApplicationId={backofficeApplication.id}
							collectedInvoicesAndNotices={collectedInvoicesAndNotices}
						/>
					</MyPagePaper>
				</Grid>
			</Grid>
		</MyPagePanel>
	);
};

interface LastInvoicesToPayInformationProps {
	label: string;
	value: string | number;
}

const LastInvoicesToPayInformation = (
	props: LastInvoicesToPayInformationProps
) => {
	const { label, value } = props;

	const isMobile = useCommonWidths().isSmDown;
	const classes = useStyles();

	return (
		<>
			<Typography variant={isMobile ? "caption" : "body1"}>{label}</Typography>
			<Typography className={classes.value}>{value}</Typography>
		</>
	);
};

export default InvoicesTabPage;
